import React from 'react';
import moment from 'moment';
import Link from 'next/link';
import { PauseSpecialNotificationSetting } from '../../../components/PauseSpecialNotificationSetting';
import { PauseTypography } from '../../../components/PauseTypography';
import { PauseAutocomplete2 } from '../../../components/PauseAutocomplete2';
import { PauseButton } from '../../../components/PauseButton';
import { SlackPHDaysInAdvanceOptions } from '../../../api/settings';
import { SLACK_PH_FREQUENCIES } from '../../../../utils/constants';
import { toCapitalCase } from '../../../../utils/strings';
import {
  SLACK_PUBLIC_HOLIDAY_SETTINGS_JOURNEY_EDIT_KEY,
  SLACK_PUBLIC_HOLIDAY_SETTINGS_JOURNEY_KEY,
  useEnhancedRouter,
} from '../../../../router';
import { PauseTable } from '../../../components/PauseTable';
import {
  PauseTableBody,
  PauseTableBodyCell,
  PauseTableRow,
} from '../../../components/PauseTable/components/PauseTableRow';

const DisabledStateText = ({ event }: { event: string }) => (
  <PauseTypography variant="body2">
    {`Notify a channel on team members' ${event}. `}
    To customize personal {event} notifications, go to{' '}
    <Link href="/profile/me/preferences/slack">
      <PauseTypography
        color="pause.primary.dark"
        css={{
          cursor: 'pointer',
          display: 'inline',
        }}
        inline
      >
        Personal Preferences
      </PauseTypography>
    </Link>
  </PauseTypography>
);
export type SpecialSettingsOptions = {
  channel: string;
  dayOfWeek: string;
  time: string;
  daysInAdvance: SlackPHDaysInAdvanceOptions;
};
type Props = {
  enabled: boolean;
  birthdays: Pick<SpecialSettingsOptions, 'channel' | 'time'> | null;
  workAnniversary: Pick<SpecialSettingsOptions, 'channel' | 'time'> | null;
  publicHolidays: SpecialSettingsOptions | null;
  onToggleSetting: (a: { isCreating?: boolean; type: string; enabled: boolean; options?: any }) => any;
  slackChannels: Array<{
    label: string;
    value: string;
    isGeneral?: boolean;
  }>;
};

const formatTime = (timeString) => moment(timeString, 'hh:mm:ss').format('h.mm a');
export const SpecialSettings = ({
  birthdays = null,
  publicHolidays = null,
  workAnniversary = null,
  onToggleSetting = () => null,
  slackChannels = [],
  enabled = false,
}: Props) => {
  const generalChannelID = React.useMemo<string>(() => slackChannels.find(({ isGeneral }) => isGeneral)?.value || '', [
    slackChannels,
  ]);
  const getChannel = React.useCallback((id) => slackChannels.find(({ value }) => value === id), [slackChannels]);
  const defaultPHOptions = React.useMemo<{
    channel: string;
    time: string;
    dayOfWeek: string;
    daysInAdvance: SlackPHDaysInAdvanceOptions;
  }>(
    () => ({
      channel: generalChannelID,
      time: '09:00:00',
      dayOfWeek: 'MONDAY',
      daysInAdvance: SlackPHDaysInAdvanceOptions.FourWeeks,
    }),
    [generalChannelID],
  );

  const birthdayChannel = getChannel(birthdays?.channel || null);
  const workAnnivChannel = getChannel(workAnniversary?.channel || null);
  const friendlyDaysInAdvance =
    SLACK_PH_FREQUENCIES.find(({ value }) => publicHolidays?.daysInAdvance === value)?.label || '';
  const router = useEnhancedRouter();
  return (
    <div
      css={(theme) => ({
        borderRadius: theme.spacing(1),
        backgroundColor: theme.get('palette.pause.white.main'),
        boxShadow: theme.pauseShadows[2],
      })}
    >
      <PauseTable>
        <PauseTableBody>
          <PauseTableRow>
            <PauseTableBodyCell>
              <PauseTypography color="pause.primary.medium" variant="subtitle">
                Special Notifications
              </PauseTypography>
            </PauseTableBodyCell>
          </PauseTableRow>
          <PauseTableRow>
            <PauseTableBodyCell>
              <PauseSpecialNotificationSetting
                disabled={!enabled}
                title="Birthdays"
                description={
                  <PauseTypography variant="body2" color="pause.primary.medium">
                    {!birthdays ? (
                      <DisabledStateText event="birthday" />
                    ) : (
                      `Birthday notifications will be posted on #${birthdayChannel?.label || ''} at ${formatTime(
                        birthdays?.time,
                      )}`
                    )}
                  </PauseTypography>
                }
                checked={!!birthdays}
                onToggleChanged={(a) => {
                  if (a) {
                    return onToggleSetting({
                      type: 'birthdays',
                      enabled: true,
                      isCreating: true,
                      options: {
                        channel: generalChannelID,
                      },
                    });
                  }
                  return onToggleSetting({
                    type: 'birthdays',
                    enabled: false,
                  });
                }}
              >
                <div
                  css={{
                    maxWidth: '25%',
                  }}
                  key={birthdayChannel?.value}
                >
                  <PauseAutocomplete2
                    options={slackChannels}
                    value={birthdayChannel}
                    label="Select channel"
                    onChange={(v) =>
                      v &&
                      onToggleSetting({
                        type: 'birthdays',
                        enabled: true,
                        options: {
                          channel: v.value,
                        },
                      })
                    }
                  />
                </div>
              </PauseSpecialNotificationSetting>
            </PauseTableBodyCell>
          </PauseTableRow>
          <PauseTableRow>
            <PauseTableBodyCell>
              <PauseSpecialNotificationSetting
                disabled={!enabled}
                title="Work anniversary"
                description={
                  <PauseTypography variant="body2" color="pause.primary.medium">
                    {!workAnniversary ? (
                      <DisabledStateText event="work anniversary" />
                    ) : (
                      `Work anniversary notifications will be posted on #${
                        workAnnivChannel?.label || ''
                      } at ${formatTime(workAnniversary?.time)}`
                    )}
                  </PauseTypography>
                }
                checked={!!workAnniversary}
                onToggleChanged={(a) => {
                  if (a) {
                    return onToggleSetting({
                      type: 'workAnniversary',
                      enabled: true,
                      isCreating: true,
                      options: {
                        channel: generalChannelID,
                      },
                    });
                  }
                  return onToggleSetting({
                    type: 'workAnniversary',
                    enabled: false,
                  });
                }}
              >
                <div
                  css={{
                    maxWidth: '25%',
                  }}
                  key={workAnnivChannel?.value}
                >
                  <PauseAutocomplete2
                    options={slackChannels}
                    value={workAnnivChannel}
                    label="Select channel"
                    onChange={(v) =>
                      v &&
                      onToggleSetting({
                        type: 'workAnniversary',
                        enabled: true,
                        options: {
                          channel: v.value,
                        },
                      })
                    }
                  />
                </div>
              </PauseSpecialNotificationSetting>
            </PauseTableBodyCell>
          </PauseTableRow>
          <PauseTableRow>
            <PauseTableBodyCell>
              <PauseSpecialNotificationSetting
                disabled={!enabled}
                title="Upcoming Public Holidays"
                description={
                  publicHolidays ? (
                    <PauseTypography variant="body2" color="pause.primary.medium">
                      Notify {`#${getChannel(publicHolidays?.channel)?.label}`} of upcoming public holidays for the next{' '}
                      {friendlyDaysInAdvance}, every {toCapitalCase(publicHolidays?.dayOfWeek || '')} at{' '}
                      {moment(publicHolidays?.time, 'hh:mm:ss').format('hh:mm a')}.
                    </PauseTypography>
                  ) : (
                    <PauseTypography variant="body2" color="pause.primary.medium">
                      Notify a channel weekly, of upcoming public holidays.
                    </PauseTypography>
                  )
                }
                checked={!!publicHolidays}
                onToggleChanged={(a) => {
                  if (a) {
                    return onToggleSetting({
                      type: 'publicHolidays',
                      enabled: true,
                      isCreating: true,
                      options: defaultPHOptions,
                    });
                  }
                  return onToggleSetting({
                    type: 'publicHolidays',
                    enabled: false,
                  });
                }}
              >
                <div
                  css={{
                    maxWidth: '25%',
                  }}
                >
                  <PauseButton
                    onClick={() =>
                      router.push(
                        router.getStopHref(
                          SLACK_PUBLIC_HOLIDAY_SETTINGS_JOURNEY_KEY,
                          SLACK_PUBLIC_HOLIDAY_SETTINGS_JOURNEY_EDIT_KEY,
                        ),
                      )
                    }
                    variant="outlined"
                  >
                    Customize
                  </PauseButton>
                </div>
              </PauseSpecialNotificationSetting>
            </PauseTableBodyCell>
          </PauseTableRow>
        </PauseTableBody>
      </PauseTable>
    </div>
  );
};
